import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { notificationTemplateApi } from '@/api/notificationTemplateApi';
import type { UpdateNotificationTemplateRequest } from '@/api/types/notificationTemplate.types';

export const notificationTemplateKeys = {
  all: ['notificationTemplates'] as const,
  list: ['notificationTemplates', 'list'] as const,
  detail: (id: number) => ['notificationTemplates', 'detail', id] as const,
};

export function useNotificationTemplates() {
  return useQuery({
    queryKey: notificationTemplateKeys.list,
    queryFn: () => notificationTemplateApi.getAll(),
  });
}

export function useNotificationTemplate(id: number) {
  return useQuery({
    queryKey: notificationTemplateKeys.detail(id),
    queryFn: () => notificationTemplateApi.getById(id),
    enabled: !!id,
  });
}

export function useUpdateNotificationTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...request }: { id: number } & UpdateNotificationTemplateRequest) =>
      notificationTemplateApi.update(id, request),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: notificationTemplateKeys.list });
      queryClient.invalidateQueries({ queryKey: notificationTemplateKeys.detail(id) });
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: notificationTemplateKeys.list });
        queryClient.invalidateQueries({ queryKey: notificationTemplateKeys.detail(id) });
      }
    },
  });
}
